import React, { useCallback, useEffect, useState, useMemo } from 'react';
import {
  List,
  Datagrid,
  TextInput,
  TextField,
  Pagination,
  SingleFieldList,
  ReferenceManyField,
  useListContext
} from 'react-admin';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  makeStyles,
  LinearProgress
} from '@material-ui/core';
import ErrorIcon from '@material-ui/icons/Error';
import {
  predictiveSearchFormatter,
  predictiveSearchParser
} from '../../core/utils';

import InvestorApi from '../../core/apis/InvestorApi';
import { InvestorTagIcon } from '../../core/themes/icons/InvestorTagIcon';
import BulkActionDeleteButton from '../../core/components/BulkActionDeleteButton';
import GridPopover from '../../core/components/grid/GridPopover';
import LinkField from '../../core/components/LinkField';
import Empty from '../../core/components/list/Empty';
import Filter from '../../core/components/list/StyledFilter';
import ListTitleBar from './investortagimport/ExcelInvestorTagImportTitleBarWithDropbox';
import useNotifyServerError from '../../core/hooks/useNotifyServerError';

const useStyles = makeStyles({
  nameColumnStyle: {
    width: '45%'
  },
  inputCustomWidth: {
    width: '25%'
  },
  formFullWidth: {
    width: '100%',
    marginTop: '0px',
    marginBottom: '-8px',
    '& > div': {
      width: '100%'
    }
  },
  tag: {
    color: '#212121',
    'font-size': '12px',
    'line-height': '12px',
    textDecoration: 'none',
    'box-sizing': 'border-box',
    background: '#F5F5F5',
    border: '1px solid #E0E0E0',
    'border-radius': '2px',
    padding: '0 5px'
  },
  investors: {
    color: '#2962FF',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  button: {
    padding: 0,
    justifyContent: 'flex-start'
  },
  progress: {
    margin: 10
  },
  errorIcon: {
    margin: '2px',
    verticalAlign: 'middle',
    color: 'red'
  }
});

const TagsTitle = props => {
  const { selectedIds } = useListContext();
  return (
    <ListTitleBar
      type="Investor Tags"
      title="Create New Investor Tag"
      createUrl="#/investortags/create"
      hasCreatePermission={props.hasCreatePermission}
      hasImportInvestorTagExcelPermission={
        props.hasImportInvestorTagExcelPermission
      }
      hasExportInvestorTagExcelPermission={
        props.hasExportInvestorTagExcelPermission
      }
      filters={props.appliedFilters}
      investorTagNameFilter={props.investorTagNameFilter}
      icon={<InvestorTagIcon />}
      selectedIds={selectedIds}
    />
  );
};

const InvestorTagNameFilters = props => {
  const classes = useStyles();

  const handleNameFilterChange = event => {
    props.setInvestorTagNameFilter(event.target.value);
  };

  return (
    <Filter {...props} variant="standard" className={classes.formFullWidth}>
      <TextInput
        label="Search by Investor Tag Name"
        onChange={handleNameFilterChange}
        source="name"
        alwaysOn
        parse={predictiveSearchParser}
        format={predictiveSearchFormatter}
        options={{ autoComplete: 'off' }}
        className={classes.inputCustomWidth}
      />
    </Filter>
  );
};

const DisplayInvestors = ({ record }) => {
  const classes = useStyles();
  const [fetchedInvestors, setFetchedInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const notifyServerError = useNotifyServerError();

  const fetchInvestors = useCallback(async () => {
    return InvestorApi.getInvestors(record.investorIds);
  }, [record.investorIds]);

  useEffect(() => {
    fetchInvestors()
      .then(res => {
        setFetchedInvestors(res);
      })
      .catch(err => {
        setError(true);
        notifyServerError(err, undefined);
      })
      .finally(() => setLoading(false));
  }, [fetchInvestors, notifyServerError]);

  function renderInvestors() {
    if (error) return <ErrorIcon className={classes.errorIcon} />;

    if (loading) return <LinearProgress classes={{ root: classes.progress }} />;

    return (
      <>
        {fetchedInvestors.map(item => {
          return (
            <TableRow key={item.id}>
              <TableCell>{item.name}</TableCell>
            </TableRow>
          );
        })}
      </>
    );
  }

  return (
    <Paper>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Investors</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{renderInvestors()}</TableBody>
      </Table>
    </Paper>
  );
};

const InvestorField = ({ record }) => {
  const classes = useStyles();

  if (record.investorIds.length > 1) {
    return (
      <GridPopover
        text={`${record.investorIds.length} investors`}
        classes={{ button: classes.button }}
        popoverContent={<DisplayInvestors record={record} />}
      />
    );
  } else if (record.investorIds.length === 1) {
    return (
      <ReferenceManyField
        source="investorIds[0]"
        reference="investors"
        target="id"
        filter={{ orphanVisibility: 'ALL_VISIBLE' }}
      >
        <SingleFieldList linkType={false}>
          <TextField source="name" />
        </SingleFieldList>
      </ReferenceManyField>
    );
  }
  return <span>0 Investors</span>;
};

const InvestorTagList = props => {
  const classes = useStyles();
  const { hasCreatePermission, hasDeletePermission, basePath } = props;

  const { hasImportInvestorTagExcelPermission } = useMemo(() => {
    return {
      hasImportInvestorTagExcelPermission: props.permissions
        ? props.permissions.includes('AUTH_IMPORT_INVESTOR_TAG_FROM_EXCEL')
        : false
    };
  }, [props.permissions]);

  const { hasExportInvestorTagExcelPermission } = useMemo(() => {
    return {
      hasExportInvestorTagExcelPermission: props.permissions
        ? props.permissions.includes('AUTH_EXPORT_INVESTOR_TAG_FROM_EXCEL')
        : false
    };
  }, [props.permissions]);

  const [investorTagNameFilter, setInvestorTagNameFilter] = useState('');

  return (
    <List
      basePath={basePath}
      hasCreate={false}
      title={
        <TagsTitle
          hasCreatePermission={hasCreatePermission}
          hasImportInvestorTagExcelPermission={
            hasImportInvestorTagExcelPermission
          }
          filters={props.appliedFilters}
          hasExportInvestorTagExcelPermission={
            hasExportInvestorTagExcelPermission
          }
          investorTagNameFilter={investorTagNameFilter}
        />
      }
      sort={{ field: 'name', order: 'ASC' }}
      exporter={false}
      perPage={20}
      filters={
        <InvestorTagNameFilters
          setInvestorTagNameFilter={setInvestorTagNameFilter}
        />
      }
      pagination={<Pagination rowsPerPageOptions={[20, 25, 30]} />}
      bulkActionButtons={
        hasDeletePermission && (
          <BulkActionDeleteButton confirmContent="ra.message.confirm_delete_tags" />
        )
      }
      empty={<Empty />}
    >
      <Datagrid classes={{ thead: hasDeletePermission }}>
        <LinkField
          label="Investor Tag Name"
          source="name"
          headerClassName={classes.nameColumnStyle}
          className={classes.tag}
        />
        <TextField label="Description" source="description" />
        <InvestorField label="Assigned To:" />
      </Datagrid>
    </List>
  );
};

export default InvestorTagList;
